/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    Component,
    NgZone,
    OnDestroy,
    OnInit,
    TemplateRef,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { debounceTime, filter, fromEvent, merge, Subscription, throttleTime } from 'rxjs';
import { NgStyle } from '@angular/common';
import { MatSidenavModule } from '@angular/material/sidenav';
import {
    FooterComponent,
    HeaderComponent,
    SidebarHeaderComponent,
    SidebarNavigationComponent,
} from '@nagel-components';
import { RouterLinkActive, RouterOutlet } from '@angular/router';
import { IconComponent, IconService } from '@nagel-assests';
import { MatListModule } from '@angular/material/list';
import { ApplicationDateAdapter, MILLISECONDS } from '@nagel-utils';
import { DEFAULT_SIDENAV_COLLAPSED_STATE, REFRESH_MESSAGE_KEY } from '@nagel-consts';
import { sidenavLink } from '@nagel-types';
import { sidenavLinks } from './configuration/definitions/navLinks';
import { getAssetsPath } from './utils/environmentUtils';
import { BranchLookupService } from './services/branchLookupService/branch-lookup.service';
import { KeycloakService, SettingsDrawerService, TabCommunicationService } from '@nagel-services';
import { environment } from 'apps/nagel-cal-disposition/environment/environment';
import {
    TransportOrderDriveInstructionsDialogComponent
} from './components/transport-order-drive-instructions-dialog/transport-order-drive-instructions-dialog.component';
import { HoverTooltipComponent } from './components/hover-tooltip/hover-tooltip.component';

const MY_DATE_FORMATS = {
    parse: {
        dateInput: 'dd.MM.yyyy',
    },
    display: {
        dateInput: 'dd.MM.yyyy',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    }
};

const ACTIVITY_THROTTLE_TIME_MILISECONDS = 1000;

const REFRESH_DEBOUNCE_TIME_MILISECONDS = 5000;

@Component({
    selector: 'app-root',
    imports: [
        MatSidenavModule,
        MatButtonModule,
        SidebarHeaderComponent,
        RouterOutlet,
        IconComponent,
        MatListModule,
        RouterLinkActive,
        HeaderComponent,
        FooterComponent,
        SidebarNavigationComponent,
        NgStyle,
        TransportOrderDriveInstructionsDialogComponent,
        HoverTooltipComponent
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    providers: [
        IconService,
        { provide: DateAdapter, useClass: ApplicationDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
    ]
})
export class AppComponent implements OnDestroy, OnInit {
    collapsed = DEFAULT_SIDENAV_COLLAPSED_STATE;
    navItems: sidenavLink[] = sidenavLinks;
    headerTemplate!: TemplateRef<any>;
    footerTemplate!: TemplateRef<any>;
    private subscription: Subscription | undefined;
    isDrawerOpened = false;
    private drawerSub!: Subscription;
    private syncSub?: Subscription;

    showDebugTrace = environment.showDebugTrace || false;

    // eslint-disable-next-line max-params
    constructor(
        private keycloakService: KeycloakService,
        private settingsDrawerService: SettingsDrawerService,
        private ngZone: NgZone,
        private iconService: IconService,
        private branchLookupService: BranchLookupService,
        private tabChannelService: TabCommunicationService
    ) {
        this.iconService.registerIcons();
        this.branchLookupService.getAvailableBranches();
        this.syncSub = this.tabChannelService.changedDateSubject
            .pipe(
                debounceTime(REFRESH_DEBOUNCE_TIME_MILISECONDS)
            )
            .subscribe((message: string) => {
                if (message === REFRESH_MESSAGE_KEY) {
                    window.location.reload();
                }

            });
    }

    getProfilePicturePath = (localeId: string): string => {
        return getAssetsPath(localeId, 'profile_picture.webp');
    };

    onActivate(componentRef: {
    headerTemplate: TemplateRef<any>;
    footerTemplate: TemplateRef<any>;
  }) {
        this.headerTemplate = componentRef.headerTemplate;
        this.footerTemplate = componentRef.footerTemplate;
    }

    ngOnInit(): void {
        this.ngZone.runOutsideAngular(() => {
            const activityObservable = merge(
                fromEvent(window, 'mousemove'),
                fromEvent(window, 'keypress'),
                fromEvent(window, 'scroll'),
                fromEvent(window, 'blur')
            ).pipe(
                throttleTime(ACTIVITY_THROTTLE_TIME_MILISECONDS),
                filter(() => {
                    const currentDateInSeconds =
                                new Date().getTime() / MILLISECONDS;

                    const tokenExpirationTimeInSeconds =
                                this.keycloakService.getParsedToken()?.exp;

                    return (
                        (tokenExpirationTimeInSeconds || currentDateInSeconds) -
                                 currentDateInSeconds < environment.sessionRenewalTime
                    );
                })
            );

            this.subscription = activityObservable.subscribe(() => {
                if (this.keycloakService.getIsTokenExpired()) {
                    this.keycloakService.logout();
                } else {
                    this.keycloakService.tryTokenUpdate(() => {
                        console.log('Token refreshed');
                    });
                }
            });
        });

        this.drawerSub = this.settingsDrawerService.isDrawerOpened$.subscribe(
            (opened: boolean) => {
                this.isDrawerOpened = opened;
            }
        );
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
        this.drawerSub?.unsubscribe();
        this.syncSub?.unsubscribe();
    }
}